import { CheckCircle2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { t } from "@/i18n/pl";
import { phoneInput } from "@/lib/format";
import { ApiError } from "@/services/api-client";
import * as authService from "@/services/auth.service";

/** Public access-request form — same shadcn card treatment as the login. */
export function SignupPage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authService.requestAccess({
        full_name: fullName,
        email,
        phone,
        privacy_accepted: privacyAccepted,
      });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t.common.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-sidebar flex min-h-svh flex-col items-center justify-center gap-8 p-6 md:p-10">
      <div className="animate-in fade-in slide-in-from-bottom-3 flex flex-col items-center gap-3 duration-500">
        <img src="/logo.png" alt="ZM SKIBA S.A." className="h-14 w-auto" />
        <p className="text-sidebar-foreground text-lg font-bold">{t.appName}</p>
      </div>

      <Card className="animate-in fade-in slide-in-from-bottom-2 w-full max-w-sm duration-500">
        {sent ? (
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <CheckCircle2 className="size-10 text-[oklch(0.55_0.12_124)]" aria-hidden />
            <h1 className="text-lg font-bold">{t.signup.sentTitle}</h1>
            <p className="text-muted-foreground text-sm">{t.signup.sentBody}</p>
            <Button asChild variant="outline" className="mt-2">
              <Link to="/login">{t.signup.backToLogin}</Link>
            </Button>
          </CardContent>
        ) : (
          <>
            <CardHeader>
              <CardTitle>{t.signup.title}</CardTitle>
              <CardDescription>{t.signup.subtitle}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={(event) => void handleSubmit(event)}>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="signup-name">{t.signup.fullName}</FieldLabel>
                    <Input
                      id="signup-name"
                      required
                      minLength={3}
                      maxLength={120}
                      autoComplete="name"
                      placeholder={t.signup.fullNamePlaceholder}
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      disabled={submitting}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="signup-email">{t.signup.email}</FieldLabel>
                    <Input
                      id="signup-email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder={t.auth.usernameEmailPlaceholder}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      disabled={submitting}
                    />
                    <FieldDescription>{t.signup.emailHint}</FieldDescription>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="signup-phone">{t.signup.phone}</FieldLabel>
                    <Input
                      id="signup-phone"
                      type="tel"
                      required
                      minLength={7}
                      maxLength={32}
                      autoComplete="tel"
                      placeholder={t.signup.phonePlaceholder}
                      value={phone}
                      onChange={(event) => setPhone(phoneInput(event.target.value))}
                      disabled={submitting}
                    />
                    <FieldDescription>{t.signup.phoneHint}</FieldDescription>
                  </Field>
                  {/* RODO information duty: a native checkbox keeps this
                      dependency-free; `required` blocks submit until ticked,
                      and the API refuses the request without it as well. */}
                  <div className="flex items-start gap-3">
                    <input
                      id="signup-privacy"
                      type="checkbox"
                      required
                      className="accent-primary mt-0.5 size-4 shrink-0"
                      checked={privacyAccepted}
                      onChange={(event) => setPrivacyAccepted(event.target.checked)}
                      disabled={submitting}
                    />
                    {/* block + min-w-0: the label's own flex layout made the
                        sentence and its link separate boxes that could not
                        wrap together, and pushed the page 3px sideways on a
                        375px phone. */}
                    <FieldLabel
                      htmlFor="signup-privacy"
                      className="text-muted-foreground block min-w-0 flex-1 text-sm leading-snug font-normal"
                    >
                      {t.signup.privacyConsentPrefix}
                      <Link
                        to="/prywatnosc"
                        target="_blank"
                        className="text-foreground underline underline-offset-2"
                      >
                        {t.signup.privacyConsentLink}
                      </Link>
                      {t.signup.privacyConsentSuffix}
                    </FieldLabel>
                  </div>
                  {error && (
                    <FieldDescription role="alert" className="text-destructive">
                      {error}
                    </FieldDescription>
                  )}
                  <Button type="submit" className="h-11" disabled={submitting}>
                    {submitting ? t.signup.submitting : t.signup.submit}
                  </Button>
                  <FieldDescription className="text-center">
                    <Link to="/login" className="underline underline-offset-2">
                      {t.signup.backToLogin}
                    </Link>
                  </FieldDescription>
                </FieldGroup>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
